import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[80vh] place-items-center px-5 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="mx-auto flex w-fit items-center gap-2">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-brand text-brand-sand">
            <Leaf className="h-5 w-5" />
          </span>
          <span className="font-heading text-2xl text-brand-ink">Ayurvedam.</span>
        </Link>

        <div className="mt-8 rounded-2xl border border-brand-line bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2 text-brand">
            <Lock className="h-5 w-5" />
            <h1 className="font-heading text-2xl text-brand-ink">Migration Admin</h1>
          </div>
          <p className="mt-1 text-sm text-brand-muted">Sign in to access the migration command center.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-brand-ink">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-brand-line bg-brand-mist/40 px-4 py-2.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-ink">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-brand-line bg-brand-mist/40 px-4 py-2.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full rounded-full bg-brand py-3 font-medium text-brand-sand transition-transform hover:scale-[1.01] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
